/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable antfu/top-level-function */
import type { ITemplateData } from '@ui/interfaces/templates'
import type { IPagesData } from '@ui/interfaces/pages'
import type { IHotelProfileData } from '@ui/interfaces/hotel'
import type { ISocMedData } from '@ui/interfaces/general'
import type { IAwardData } from '@ui/interfaces/awards'
import type { IDiningData } from '@ui/interfaces/dinings'
import type { IFacilityData } from '@ui/interfaces/facilities'
import type { IEventsData } from '@ui/interfaces/events'
import type { IMeetingsData } from '@ui/interfaces/meetings'
import type { ITermsData } from '@ui/interfaces/terms'
import type { IRoomsData } from '@ui/interfaces/rooms'
import type { IGalleryData } from '@ui/interfaces/galleries'
import type { ILabelsData } from '@ui/interfaces/labels'
import type { ISlideshowData } from '@ui/interfaces/slideshow'
import type { IPressData } from '@ui/interfaces/press'
import type { IOffersCategoriesData, IOffersData } from '@ui/interfaces/offers'
import type { IRecruitmentData } from '@ui/interfaces/recruitment'
import type { IAttractionData } from '@ui/interfaces/attractions'
import type { IGeneralConfigParams } from '@ui/interfaces/generalConfig'
import type { IAdditional } from 'pinia-store/additional'
import { ENDPOINT_URL } from 'pinia-store/helpers/api'

const hotelId = import.meta.env.VITE_APP_HOTEL_ID
const isDev = (import.meta.env.NODE_ENV) === 'development'

const endpoints = {
  // GET
  hotel: `${ENDPOINT_URL}/hotel/${hotelId}`,
  hotelTemplate: `${ENDPOINT_URL}/hotel/${hotelId}/template`,
  pages: `${ENDPOINT_URL}/pages`,
  socialMedia: `${ENDPOINT_URL}/social-media`,
  slideshow: `${ENDPOINT_URL}/hotel/${hotelId}/slideshow`,
  awards: `${ENDPOINT_URL}/awards`,
  dinings: `${ENDPOINT_URL}/dinings`,
  languages: `${ENDPOINT_URL}/languages`,
  events: `${ENDPOINT_URL}/events`,
  meetings: `${ENDPOINT_URL}/meetings`,
  facilities: `${ENDPOINT_URL}/facilities`,
  generalTerms: `${ENDPOINT_URL}/v2/terms`,
  terms: `${ENDPOINT_URL}/terms`,
  attraction: `${ENDPOINT_URL}/attractions`,
  rooms: `${ENDPOINT_URL}/rooms`,
  offers: `${ENDPOINT_URL}/offers`,
  offersCategory: `${ENDPOINT_URL}/offers-category`,
  news: `${ENDPOINT_URL}/news`,
  labels: `${ENDPOINT_URL}/labels?hotel_id=${hotelId}`,
  galleries: `${ENDPOINT_URL}/hotel/${hotelId}/gallery`,
  recruitment: `${ENDPOINT_URL}/recruitments?hotel_id=${hotelId}`,
  generalConfig: `${ENDPOINT_URL}/general-config?hotel_id=${hotelId}`,
  additional: `${ENDPOINT_URL}/additional-pages?hotel_id=${hotelId}`,
}

// Add build counter tracking
const buildCounters: Record<string, number> = {}

// Helper function to increment counter for specific function
const incrementBuildCount = (functionName: string): void => {
  if (!isDev) {
    buildCounters[functionName] = (buildCounters[functionName] || 0) + 1
    console.log(`[BUILD_COUNT] in "${functionName}"`, buildCounters[functionName])

    // Log total counts after each increment
    logTotalBuildCounts()
  }
}

// Helper function to log total build counts
const logTotalBuildCounts = (): void => {
  console.log('\n[BUILD_COUNT] Total counts per function:')
  console.log('----------------------------------------')
  Object.entries(buildCounters)
    .sort(([, a], [, b]) => b - a) // Sort by count in descending order
    .forEach(([functionName, count]) => {
      console.log(`${functionName}: ${count} calls`)
    })
  console.log('----------------------------------------')

  // Calculate and log the total of all counts
  const totalCalls = Object.values(buildCounters).reduce((sum, count) => sum + count, 0)
  console.log(`Total API calls across all functions: ${totalCalls}\n`)
}

// Export the function to manually check totals
export const getTotalBuildCounts = (): void => {
  logTotalBuildCounts()
}

// Exports
export async function fetchTemplates(headers, query) {
  incrementBuildCount('fetchTemplates')
  return await useLazyFetch<ITemplateData>(endpoints.hotelTemplate, {
    key: 'hotelTemplate',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchPages(headers, query) {
  incrementBuildCount('fetchPages')
  return await useLazyFetch<IPagesData>(endpoints.pages, {
    key: 'pages',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchHotel(headers, query) {
  incrementBuildCount('fetchHotel')
  return await useLazyFetch<IHotelProfileData>(endpoints.hotel, {
    key: 'hotel',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchSocialMedia(headers, query) {
  incrementBuildCount('fetchSocialMedia')
  return await useLazyFetch<ISocMedData>(endpoints.socialMedia, {
    key: 'socialMedia',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchAwards(headers, query) {
  incrementBuildCount('fetchAwards')
  return await useLazyFetch<IAwardData>(endpoints.awards, {
    key: 'awards',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchDinings(headers, query) {
  incrementBuildCount('fetchDinings')
  return await useLazyFetch<IDiningData>(endpoints.dinings, {
    key: 'dinings',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchFacilities(headers, query) {
  incrementBuildCount('fetchFacilities')
  return await useLazyFetch<IFacilityData>(endpoints.facilities, {
    key: 'facilities',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchEvents(headers, query) {
  incrementBuildCount('fetchEvents')
  return await useLazyFetch<IEventsData>(endpoints.events, {
    key: 'events',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchMeetings(headers, query) {
  incrementBuildCount('fetchMeetings')
  return await useLazyFetch<IMeetingsData>(endpoints.meetings, {
    key: 'meetings',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchTerms(headers, query) {
  incrementBuildCount('fetchTerms')
  return await useLazyFetch<ITermsData>(endpoints.terms, {
    key: 'terms',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchRooms(headers, query) {
  incrementBuildCount('fetchRooms')
  return await useLazyFetch<IRoomsData>(endpoints.rooms, {
    key: 'rooms',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchGalleries(headers, query) {
  incrementBuildCount('fetchGalleries')
  return await useLazyFetch<IGalleryData>(endpoints.galleries, {
    key: 'galleries',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchAllLabels(headers, query) {
  incrementBuildCount('fetchAllLabels')
  return await useLazyFetch<ILabelsData>(endpoints.labels, {
    key: 'labels',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchOffers(headers, query) {
  incrementBuildCount('fetchOffers')
  return await useLazyFetch<IOffersData>(endpoints.offers, {
    key: 'offers',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchAttraction(headers, query) {
  incrementBuildCount('fetchAttraction')
  return await useLazyFetch<IAttractionData>(endpoints.attraction, {
    key: 'attraction',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchLanguages(headers, query) {
  incrementBuildCount('fetchLanguages')
  return await useLazyFetch<IAttractionData>(endpoints.languages, {
    key: 'languages',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchOffersCategory(headers, query) {
  incrementBuildCount('fetchOffersCategory')
  return await useLazyFetch<IOffersCategoriesData>(endpoints.offersCategory, {
    key: 'offersCategory',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchNews(headers, query) {
  incrementBuildCount('fetchNews')
  return await useLazyFetch<IPressData>(endpoints.news, {
    key: 'news',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchSlideshow(headers, query) {
  incrementBuildCount('fetchSlideshow')
  return await useLazyFetch<ISlideshowData>(endpoints.slideshow, {
    method: 'GET',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    headers,
    query,
  })
}

export async function fetchRecruitment(headers, query) {
  incrementBuildCount('fetchRecruitment')
  return await useLazyFetch<IRecruitmentData>(endpoints.recruitment, {
    key: 'recruitment',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchGeneralConfig(headers, query) {
  incrementBuildCount('fetchGeneralConfig')
  return await useLazyFetch<IGeneralConfigParams>(endpoints.generalConfig, {
    key: 'generalConfig',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchAdditionalPage(headers, query) {
  incrementBuildCount('fetchAdditionalPage')
  return await useLazyFetch<IAdditional>(endpoints.additional, {
    key: 'additional',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}

export async function fetchGeneralTermsPage(headers, query) {
  incrementBuildCount('fetchGeneralTermsPage')
  return await useLazyFetch<IAdditional>(endpoints.generalTerms, {
    key: 'generalTerms',
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
    method: 'GET',
    headers,
    query,
  })
}
